import { useQueryClient } from '@tanstack/react-query'
import { Form, Typography } from 'antd'
import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import {
  IBodyProfile,
  useGetAvatar,
  useGetProfile,
  useUpdateProfile,
  useUploadAvatar,
  useUploadOnboardingForm,
} from '../../api/profile'
import { COLOR_STATUS_USER, COLOR_USER_STATUS, OnboardingStatus, STATUS_USER_ENUM, USER_STATUS } from '../../App.constants'
import { ONBOARDING_STATUS_KEY } from '../../components/providers/authProvider'
import avatarDefault from '../../common/images/svg/UserCircleDefault.svg'
import CustomBreadCrumb from '../../components/breadCrumb/breadCrumb'
import { ButtonOutlinedCustom } from '../../components/button/buttonOulined'
import LoadingOverlay from '../../components/loading/loadingOverlay'
import ConfirmModal from '../../components/modal/confirmModal'
import { useNotification } from '../../components/providers/notification/useNotification'
import { refreshAvatar } from '../../store/use-refetch-avatar'
import { setTitleHeader } from '../../store/use-title-header-store'
import DetailInfo from './detail-info'
import './profile.css'

const breadCrumb = [
  { name: 'Portfolio', path: '/' },
  { name: 'Update Profile', path: '/profile' },
]

const requiredFields = [
  'email',
  'fullName',
  'regNo',
  'country',
  'address',
  'website',
]

const MAX_FILE_SIZE_MB = 10
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp']

export const ThemedProfilePage: React.FC<any> = () => {
  const [form] = Form.useForm<IBodyProfile>()
  const statusUser = localStorage.getItem('status_user')
  const uploadRef = useRef<any>(null)
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [status, setStatus] = useState<any>(null)
  const [imageUrl, setImageUrl] = useState<any>(null)
  const [fileImage, setFileImage] = useState<any>()
  const [values, setValues] = useState<IBodyProfile>()
  const [avatarUUID, setAvatarUUID] = useState<string>('')
  const [avatarURL, setAvatarURL] = useState<string>('')
  const [showConfirmModal, setShowModalControl] = useState(false)
  const [isDirty, setIsDirty] = useState(false)
  const [isAllFieldsFilled, setIsAllFieldsFilled] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [isUpdated, setIsUpdated] = useState(false)
  /** Onboarding form file selected but not yet uploaded (upload on Submit) */
  const [onboardingFormFile, setOnboardingFormFile] = useState<File | null>(
    null
  )
  /** User removed existing onboarding file and has not selected a new one */
  const [onboardingFormRemoved, setOnboardingFormRemoved] = useState(false)

  const { open: openNotification } = useNotification()

  const { data: infoProfile = { data: { data: {} } }, isFetching, refetch: refetchInfoProfile } =
    useGetProfile()

  const { data: avatar = { data: { data: { url: '' } } } } =
    useGetAvatar(avatarUUID)

  const {
    mutate: updateProfileMutate,
    mutateAsync: updateProfileAsync,
    isLoading: loadingUpdateProfile,
  } = useUpdateProfile({
    onSuccess: (response: { data: { message: string } }) => {
      setIsDirty(false)
      if (openNotification) {
        openNotification?.({
          key: 'update_profile_success',
          message: response.data.message || 'Update profile successfully!',
          type: 'success',
        })
      }
      refreshAvatar()
      queryClient.invalidateQueries(['/api/v1/auth/profile'])
      setIsUpdated(true)
      navigate('/home')
    },
    onError: (error: { message?: string; code?: string; statusCode?: number; response?: { status?: number } }) => {
      const isUnauthorized =
        error?.statusCode === 401 ||
        error?.response?.status === 401 ||
        error?.code === '401'
      if (error?.code === 'ERR00122' || error?.message === 'ERR00122') {
        if (openNotification) {
          openNotification({
            key: 'update_profile_failure',
            message: error?.message || 'Update profile failed!',
            type: 'error',
          })
          refetchInfoProfile()
          return
        }
      } else if (isUnauthorized) {
        return
      } else if (openNotification) {
        openNotification({
          key: 'update_profile_failure',
          message: error?.message || 'Update profile failed!',
          type: 'error',
        })
      }
    },
  })

  const {
    mutateAsync: uploadProfilePicAsync,
    isLoading: isLoadingUploadProfilePic,
  } = useUploadAvatar({
    onError: (error: { message: string }) => {
      if (openNotification) {
        openNotification({
          key: 'upload_avatar_failure',
          message: error?.message || 'Upload Avatar failed!',
          type: 'error',
        })
      }
    },
  })

  const {
    mutateAsync: uploadOnboardingFormAsync,
    isLoading: isLoadingOnboardingForm,
  } = useUploadOnboardingForm({
    onError: (error: { message: string }) => {
      if (openNotification) {
        openNotification({
          key: 'upload_onboarding_failure',
          message: error?.message || 'Upload onboarding form failed!',
          type: 'error',
        })
      }
    },
  })

  const beforeUpload = (file: File) => {
    const isImage = ALLOWED_IMAGE_TYPES.includes(file.type)
    const isLt10MB = file.size / 1024 / 1024 <= MAX_FILE_SIZE_MB

    if (!isImage) {
      openNotification?.({
        key: 'upload_avatar_invalid_type',
        message:
          'Invalid file type. Only JPG, PNG and WEBP images are allowed.',
        type: 'error',
      })
      return false
    }

    if (!isLt10MB) {
      openNotification?.({
        key: 'upload_avatar_failure',
        message: 'Only images up to 10MB are allowed',
        type: 'error',
      })
      return false
    }

    return true
  }

  const onSubmit = async (values: IBodyProfile) => {
    const existingOnboardingUuid = infoProfile?.data?.data?.onboardingFormUuid
    if (
      existingOnboardingUuid &&
      onboardingFormRemoved &&
      !onboardingFormFile
    ) {
      openNotification?.({
        key: 'onboarding_required',
        message: 'You must have an Onboarding Form file. Please upload a file or keep the existing one.',
        type: 'error',
      })
      return
    }

    setValues(values)
    const { smartUID, ...body } = values
    let avatarUuid: string | undefined
    let onboardingFormUuid: string | undefined
    let onboardingFileName: string | undefined

    try {
      if (fileImage) {
        const res = await uploadProfilePicAsync(fileImage) as {
          data?: { data?: { uuid?: string } }
        }
        avatarUuid = res?.data?.data?.uuid
      }
      if (onboardingFormFile) {
        const formData = new FormData()
        formData.append('file', onboardingFormFile)
        const res = await uploadOnboardingFormAsync(formData) as {
          data?: { data?: { uuid?: string } }
        }
        onboardingFormUuid = res?.data?.data?.uuid
        onboardingFileName = onboardingFormFile.name
      }

      await updateProfileAsync({
        ...body,
        ...(avatarUuid !== undefined && avatarUuid !== '' && { avatar_uuid: avatarUuid }),
        ...(onboardingFormUuid !== undefined &&
          onboardingFormUuid !== '' && {
          onboardingFormUuid
        }),
        ...(onboardingFileName !== undefined &&
          onboardingFileName !== '' && {
          onboardingFileName,
        }),
      })
      setOnboardingFormFile(null)
      setOnboardingFormRemoved(false)
    } catch {
      // onError of mutations already shows notification
    }
  }

  const handleButtonClick = () => {
    setIsUploading(true)
    uploadRef.current?.click()
  }

  const checkAllFieldsFilled = () => {
    setIsUpdated(false)
    const values = form.getFieldsValue(requiredFields as any)
    return requiredFields.every((field) => !!values[field])
  }

  const handleFileChange = (e: any) => {
    const file = e.target.files[0]
    if (file) {
      if (!beforeUpload(file)) {
        e.target.value = ''
        return
      }
      setIsUpdated(false)
      setImageUrl(URL.createObjectURL(file))
      const formData = new FormData()
      formData.append('file', file)
      setFileImage(formData)
      setIsAllFieldsFilled(checkAllFieldsFilled())
    }
    e.target.value = ''
  }

  const handleCancelConfirm = () => {
    setShowModalControl(false)
  }

  const handleSubmitConfirm = () => {
    navigate('/home')
  }

  /**
   * Store selected onboarding file (upload runs on Submit).
   * Uploading a new file = replace current file, equivalent to remove old + upload new.
   */
  const handleSelectOnboardingForm = (file: File) => {
    setIsUpdated(false)
    setOnboardingFormFile(file)
    setOnboardingFormRemoved(false)
    setIsAllFieldsFilled(checkAllFieldsFilled())
  }

  /** Clear selected (pending) onboarding file */
  const handleRemoveOnboardingForm = () => {
    setOnboardingFormFile(null)
    setOnboardingFormRemoved(true)
    setIsAllFieldsFilled(checkAllFieldsFilled())
  }

  /** User removed existing (server) onboarding file; must upload new one before save */
  const handleRemoveExistingOnboardingForm = () => {
    setOnboardingFormRemoved(true)
    setIsAllFieldsFilled(checkAllFieldsFilled())
  }

  useEffect(() => {
    if (!infoProfile) return
    setAvatarUUID(infoProfile.data?.data?.avatar_uuid)
    const wcStatus = infoProfile?.data?.data?.worldCheckVerification?.status
    const onboardingStatus = infoProfile?.data?.data?.onboardingStatus
    const status = infoProfile?.data?.data?.status
    if (wcStatus) {
      if (
        !statusUser ||
        (statusUser !== wcStatus && !isFetching)
      ) {
        localStorage.setItem('status_user', wcStatus)
      }
    }
    if (status) {
      setStatus(status)
    }
    if (onboardingStatus) {
      localStorage.setItem(ONBOARDING_STATUS_KEY, onboardingStatus)
    }
  }, [infoProfile, statusUser, isFetching])

  useEffect(() => {
    setTitleHeader('Profile Settings')
  }, [])

  useEffect(() => {
    const environment = process.env.REACT_APP_ENVIRONMENT
    setAvatarURL(
      environment === 'development'
        ? avatar.data.data.url.replace(
          'http://172.188.80.191:10000/',
          'https://azurite.dev.smart.pixitech.asia/'
        )
        : avatar.data.data.url
    )
  }, [avatar])

  useEffect(() => {
    if (isUploading) {
      const timeout = setTimeout(() => {
        setIsUploading(false)
      }, 1000)
      return () => clearTimeout(timeout)
    }
  }, [isUploading])

  return (
    <div className='profile-container'>
      <CustomBreadCrumb breadCrumb={breadCrumb} showHome={true} />
      <div className='profile-content'>
        <div className='profile-info'>
          <div className='info-title'>
            <Typography className='title'>Update Profile</Typography>
          </div>
          <div className='info-avt'>
            <img
              className='avt'
              alt='avatar'
              src={imageUrl || avatarURL || avatarDefault}
            />
            <ButtonOutlinedCustom
              size='large'
              width={'136px'}
              text={'Upload Photo'}
              onClick={handleButtonClick}
              className={isUploading ? 'no-hover' : ''}
            />
            <input
              ref={uploadRef}
              style={{ display: 'none' }}
              type='file'
              accept='image/*'
              onChange={handleFileChange}
            />
          </div>
          <DetailInfo
            form={form}
            onSubmit={onSubmit}
            info={infoProfile.data.data}
            setIsDirty={setIsDirty}
            isDirty={isDirty}
            setShowModalControl={setShowModalControl}
            isAllFieldsFilled={isAllFieldsFilled}
            setIsAllFieldsFilled={setIsAllFieldsFilled}
            checkAllFieldsFilled={checkAllFieldsFilled}
            isSuccess={isUpdated}
            onboardingStatus={infoProfile?.data?.data?.onboardingStatus}
            onboarding_form_uuid={infoProfile?.data?.data?.onboardingFormUuid}
            onboarding_file_name={
              infoProfile?.data?.data?.onboardingFileName ?? undefined
            }
            onSelectOnboardingForm={handleSelectOnboardingForm}
            onRemoveOnboardingForm={handleRemoveOnboardingForm}
            onRemoveExistingOnboardingForm={handleRemoveExistingOnboardingForm}
            onboardingFormRemoved={onboardingFormRemoved}
            pendingOnboardingFileName={onboardingFormFile?.name ?? null}
            isUploadingOnboarding={
              isLoadingUploadProfilePic ||
              isLoadingOnboardingForm ||
              loadingUpdateProfile
            }
          />
        </div>
      </div>
      <ConfirmModal
        type='warning'
        title='Unsaved Changes'
        description='You have unsaved changes. Are you sure you want to leave this page?'
        open={!!showConfirmModal}
        onCancel={handleCancelConfirm}
        onSubmit={handleSubmitConfirm}
      />
      <LoadingOverlay
        loading={
          loadingUpdateProfile ||
          isLoadingUploadProfilePic ||
          isLoadingOnboardingForm
        }
      />
    </div>
  )
}
